<script>
import { mapActions, mapState } from "vuex";
import ModalLimitExceeded from "./modalLimitExceeded.vue";
import axios from "axios";

export default {
  components: { ModalLimitExceeded },
  name: "dropdownAccommodations",

  computed: {
    ...mapState("property", ["property", "properties"]),
    ...mapState("user", ["user"]),

    userRoot() {
      return this.$store.state.userRoot || {};
    },

    isDemo() {
      const u = this.userRoot || {};
      return (
        String(u.plan || "").toLowerCase() === "free" ||
        String(u.status_subscription || "").toLowerCase() === "demo"
      );
    },

    isSubUser() {
      return !!this.$store.state.subUser;
    },

    // Nombre visible en el nav
    ownerLabel() {
      const subUser = this.$store.state.subUser;
      if (subUser?.name) return String(subUser.name).trim();
      return String(this.$store.state.userRoot?.name || "").trim();
    },

    // Inicial para el avatar
    ownerInitial() {
      return this.ownerLabel.charAt(0).toUpperCase() || "?";
    },

    // ── Tipo de panel ──────────────────────────────
    // Propiedades del store (cargan en el arranque, ANTES que entitlements/listings):
    // sirven como señal temprana para normalizar el panel y evitar la carrera que
    // abría el calendario de apartamento en una cuenta de hotel.
    storeProperties() {
      return this.$store.state.property?.properties || [];
    },

    // El tipo de panel se decide SOLO por los ANUNCIOS visibles (`/listings`), nunca por
    // billing (`entitlements`) ni por el store (`/retrive-properties`): ambos incluyen
    // plantillas huérfanas/legado (p.ej. un 'Hotel Hilton' con listing_id=NULL que NO
    // aparece en "Mis alojamientos") que falseaban el tipo y hacían salir el switcher de
    // hotel en cuentas de solo apartamentos.
    hasApartmentType() {
      return this.listings.some(l => !this.listingIsHotel(l));
    },

    hasHotelType() {
      return this.listings.some(l => this.listingIsHotel(l));
    },

    // Mostrar el switcher SOLO si el usuario tiene AMBOS tipos (apartamento + hotel):
    // así hay una elección real. Si solo tiene uno, no se ofrece la vista del otro
    // (vacía) y se fuerza el tipo correcto en normalizePanelType().
    showPanelSwitcher() {
      return this.hasHotelType && this.hasApartmentType;
    },

    /**
     * ¿Hay que reservar el sitio del selector mientras se averigua?
     *
     * Saber si la cuenta tiene hotel Y apartamento exige dos llamadas. Hasta que
     * responden, el bloque no existe y todo el menú de abajo está más arriba: al
     * llegar la respuesta baja de golpe y el clic aterriza en la opción equivocada.
     * Pasa a diario.
     *
     * Se reserva SOLO cuando ya sabemos que el selector va a salir. Reservar «por si
     * acaso» mientras no se sabe arreglaría el salto de quien tiene los dos tipos,
     * pero se lo crearía a quien solo tiene apartamentos —la mayoría—, que hoy no lo
     * sufre: vería un hueco que se desploma al no llenarse. Nunca se enseña un hueco
     * que no se va a llenar.
     *
     * La consecuencia aceptada: la primera vez que se abre el menú tras entrar, cuando
     * todavía no hay nada recordado, el salto sigue. A partir de ahí no vuelve, y
     * abrir el menú se hace muchas más veces que iniciar sesión.
     */
    esperandoPanelSwitcher() {
      return this.loadingEntitlements && this.panelSwitcherRecordado === true;
    },

    esPanelHotel() {
      return (this.$store.state.panelType || "apartment") === "hotel";
    },
    panelTypeLabel() {
      return this.activePanelType === "hotel" ? this.$t("accountMenu.panel.hotel") : this.$t("accountMenu.panel.apartment");
    },
  },

  watch: {
    // En cuanto cargan las propiedades del store (al arranque) normalizamos el tipo de
    // panel, sin esperar a entitlements/listings. Evita la carrera que abría el
    // calendario de apartamento en una cuenta de hotel.
    storeProperties: {
      immediate: true,
      handler(list) {
        if (Array.isArray(list) && list.length) this.normalizePanelType();
      },
    },
  },

  mounted() {
    this.getProperties();

    /* Lo que se supo la última vez, para acertar con el hueco ya en el primer
       pintado. El login hace localStorage.clear(), así que tras entrar se vuelve a
       aprender en la primera carga. */
    try {
      const recordado = localStorage.getItem("wc_panel_switcher");
      if (recordado === "1") this.panelSwitcherRecordado = true;
      else if (recordado === "0") this.panelSwitcherRecordado = false;
    } catch (e) {
      this.panelSwitcherRecordado = null;
    }

    this.fetchEntitlements();

    // Restaurar tipo de panel guardado
    const saved = localStorage.getItem("wc_panel_type");
    if (saved === "hotel" || saved === "apartment") {
      this.activePanelType = saved;
    }

    $(".dropdown-menu").on("click", function (e) {
      e.stopPropagation();
    });
  },

  data() {
    return {
      navM: false,
      activePanelType: "apartment", // 'apartment' | 'hotel'

      entitlements: null,
      listings: [], // lista plana de anuncios para detectar property_type
      details: { hotel: [], camping: [] },
      createdCount: { hotel: 0, camping: 0 },
      loadingEntitlements: false,
      // Lo que respondió la última vez: true, false o null si aún no se sabe.
      panelSwitcherRecordado: null,
    };
  },

  methods: {
    ...mapActions("property", ["getProperties"]),

    // ¿El usuario puede ver/usar la función `key`? (anfitrión → siempre true)
    can(key) {
      return this.$store.getters.hasFeature(key);
    },

    // ¿El anuncio es tipo hotel? Discriminador FIABLE = tiene alguna unidad type='room'
    // (el backend lo expone en `is_hotel`). Fallback a property_type (hotel/camping/hostel)
    // para hoteles recién creados aún sin unidades, o si el backend no envía el flag.
    listingIsHotel(l) {
      if (l && typeof l.is_hotel === "boolean") return l.is_hotel;
      const HOT = ["hotel", "camping", "hostel"];
      return HOT.includes(String((l && l.property_type) || "").toLowerCase().trim());
    },

    // ── Entitlements ───────────────────────────────
    async fetchEntitlements() {
      this.loadingEntitlements = true;
      try {
        const [entRes, listRes] = await Promise.allSettled([
          axios.get("/account/purchased-properties", { params: { compact: 1 } }),
          axios.get("/listings"),
        ]);

        if (entRes.status === 'fulfilled') {
          const e = entRes.value.data?.entitlements || {};
          this.entitlements = {
            apartment: { used: Number(e?.apartment?.used || 0), purchased: Number(e?.apartment?.purchased || 0), remaining: Math.max(0, Number(e?.apartment?.remaining || 0)) },
            hotel:     { used: Number(e?.hotel?.used || 0),     purchased: Number(e?.hotel?.purchased || 0),     remaining: Math.max(0, Number(e?.hotel?.remaining || 0)) },
            villa:     { used: Number(e?.villa?.used || 0),     purchased: Number(e?.villa?.purchased || 0),     remaining: Math.max(0, Number(e?.villa?.remaining || 0)) },
            camping:   { used: Number(e?.camping?.used || 0),   purchased: Number(e?.camping?.purchased || 0),   remaining: Math.max(0, Number(e?.camping?.remaining || 0)) },
            chatbot:   { used: Number(e?.chatbot?.used || 0),   purchased: Number(e?.chatbot?.purchased || 0),   remaining: Math.max(0, Number(e?.chatbot?.remaining || 0)) },
          };
        }

        // Cargar listings para detectar property_type aunque las unidades sean 0
        if (listRes.status === 'fulfilled') {
          const d = listRes.value.data;
          this.listings = d?.listings || d?.data || (Array.isArray(d) ? d : []);
        }

        // Con el inventario ya cargado, normalizar el tipo de panel: si el usuario
        // tiene un solo tipo, forzarlo (evita quedar atrapado en la vista por defecto).
        this.normalizePanelType();

        // Ya se sabe: se recuerda para la próxima vez que se abra el menú.
        this.panelSwitcherRecordado = this.showPanelSwitcher;
        try {
          localStorage.setItem("wc_panel_switcher", this.showPanelSwitcher ? "1" : "0");
        } catch (e) {
          // Sin almacenamiento el menú sigue funcionando; solo se pierde el acierto.
        }
      } catch (_) {
        this.entitlements = null;
      } finally {
        this.loadingEntitlements = false;
      }
    },

    // ── Normalizar tipo de panel según inventario ──
    // Si el usuario tiene EXACTAMENTE un tipo (solo hotel o solo apartamento), se
    // fuerza el panel a ese tipo. Con ambos (o sin datos aún) se respeta la elección
    // guardada. Solo toca el store (Sidebar reacciona); no recarga.
    normalizePanelType() {
      const hasApt = this.hasApartmentType;
      const hasHotel = this.hasHotelType;
      if (hasApt === hasHotel) return; // ambos o ninguno → no forzar
      const forced = hasHotel ? "hotel" : "apartment";
      if (this.activePanelType !== forced) this.activePanelType = forced;
      if (this.$store.state.panelType !== forced) {
        this.$store.commit && this.$store.commit("setPanelType", forced);
      }
    },

    // ── Panel switcher ─────────────────────────────
    switchPanel(type) {
      if (this.activePanelType === type) return;
      this.activePanelType = type;
      localStorage.setItem("wc_panel_type", type);
      this.$store.commit && this.$store.commit("setPanelType", type);
      this.closeDropdown();
      // Recargar dashboard con el nuevo tipo de panel
      if (this.$route.name !== "Dashboard") {
        this.$router.push({ name: "Dashboard" });
      } else {
        location.reload();
      }
    },

    // ── Navegación ─────────────────────────────────
    closeDropdown() {
      $(this.$refs.dropdown).find(".dropdown-toggle").dropdown("hide");
    },

    goRoute(routeSelect) {
      this.$router.push({ name: routeSelect });
      this.closeDropdown();
    },

    goListingManager(query = {}) {
      this.$router.push({ name: "ListingManager", query });
      this.closeDropdown();
    },

    goAiAgent() {
      this.$router.push({ name: "AgentIAManager" });
      this.closeDropdown();
    },

    goCreateUnit() {
      this.$router.push({ name: "firstCreatePropiedad" });
      this.closeDropdown();
    },

    goCreateListing() {
      this.$router.push({ name: "ListingManager", query: { action: "create" } });
      this.closeDropdown();
    },

    activeNav() {
      this.navM = !this.navM;
    },

    // Navegación del menú móvil propio: cierra el menú y navega.
    /**
     * El calendario, al que toque según el tipo de panel.
     *
     * Hotel → el calendario hotelero. Apartamento en móvil → el MENSUAL, que es
     * el que cabe en una pantalla estrecha; la vista por filas necesita arrastrar
     * de lado para ver una semana.
     *
     * Es la misma decisión que toma el sidebar de escritorio. Va aparte a
     * propósito en vez de empujar un nombre de ruta fijo: con un nombre fijo, un
     * cliente de hotel acabaría en el calendario de apartamentos.
     */
    irAlCalendario() {
      this.navM = false;
      // El múltiple es el de partida, también en el teléfono: es la vista que
      // enseña todos los alojamientos de una. Si el anfitrión prefirió el
      // mensual la última vez, se respeta.
      let guardada = null;
      try { guardada = localStorage.getItem("reservasCalendar:view"); } catch (e) { /* sin storage */ }

      const destino = this.esPanelHotel
        ? "hotelReservationCalendar"
        : (guardada === "monthly" ? "monthlyCalendar" : "ReservationCalendar");

      if (this.$route.name !== destino) this.$router.push({ name: destino });
    },

    goRouteM(name) {
      this.navM = false;
      if (this.$route.name !== name) this.$router.push({ name });
    },

    logout() {
      this.$store.dispatch("destroyToken").then(() => {
        window.location.href = "/";
      });
    },
  },
};
</script>

<template>
  <div class="the-navbar__user-meta">
    <div class="dropdown" ref="dropdown">

      <!-- ── TRIGGER ─────────────────────────────────────────── -->
      <div
        class="dd-trigger dropdown-toggle"
        data-toggle="dropdown"
        aria-expanded="false"
        @click.prevent="activeNav()"
      >
        <!-- Avatar -->
        <div class="dd-avatar">
          <div
            v-if="userRoot.logo"
            class="dd-avatar__img"
            :style="{ backgroundImage: `url(${$base.server + userRoot.logo})` }"
          />
          <div v-else class="dd-avatar__letter">{{ ownerInitial }}</div>
        </div>

        <!-- Nombre + rol -->
        <div class="dd-trigger__info" v-if="!$isMobile">
          <span class="dd-trigger__name">{{ ownerLabel }}</span>
          <span class="dd-trigger__role">
            {{ isSubUser ? $t('accountMenu.roles.collaborator') : $t('accountMenu.roles.hostMode') }}
          </span>
        </div>

        <!-- Hamburger -->
        <svg class="dd-trigger__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <line x1="3" y1="6"  x2="21" y2="6"/>
          <line x1="3" y1="12" x2="21" y2="12"/>
          <line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
      </div>

      <!-- ── Menú móvil propio (fiable: no depende de Bootstrap ni de .xs) ── -->
      <div v-if="navM && $isMobile" class="ddm-overlay" @click.self="navM = false">
        <div class="ddm-panel">
          <div class="ddm-head">
            <div class="ddm-user">
              <div class="ddm-user__avatar">{{ ownerInitial }}</div>
              <div>
                <div class="ddm-user__name">{{ ownerLabel }}</div>
                <div class="ddm-user__role">{{ isSubUser ? $t('accountMenu.roles.collaborator') : $t('accountMenu.roles.hostMode') }}</div>
              </div>
            </div>
            <button class="ddm-close" type="button" :aria-label="$t('accountMenu.actions.close')" @click="navM = false">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>

          <!--
            El menú de móvil, con los mismos iconos que el de escritorio.

            Antes iba con iconos de FontAwesome mientras el de escritorio usaba
            SVG en línea, y en morado de Argon (#5e72e4) en vez del de la marca:
            eran dos menús de dos productos distintos. Ahora comparten dibujo,
            color y tratamiento —el mismo chip de icono con fondo tintado que se
            usa en todo el panel— y van agrupados, que era lo que faltaba para
            que se leyera algo en una lista de siete enlaces seguidos.
          -->
          <nav class="ddm-items">
            <p class="ddm-group">{{ $t('accountMenu.groups.navigate') }}</p>

            <!-- 🚨 EL CALENDARIO, que en el móvil no estaba.
                 Un propietario entra desde el teléfono, solo tiene permiso para el
                 calendario y las analíticas… y en este menú no había ninguna de las
                 dos. Lo único que podía tocar era «Cerrar sesión».
                 Va la primera porque para quien solo tiene esto, es su pantalla. -->
            <a v-if="can('calendar')" href="#" class="ddm-item" @click.prevent="irAlCalendario()">
              <span class="ddm-item__icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="4.5" width="18" height="16.5" rx="2"/>
                  <path d="M8 2.6v3.8M16 2.6v3.8M3 10h18"/>
                </svg>
              </span>
              {{ $t('sidebar.items.calendar') }}
            </a>

            <a v-if="can('analytics')" href="#" class="ddm-item" @click.prevent="goRouteM('Analytics')">
              <span class="ddm-item__icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 3v18h18"/><path d="M7 15l3.5-4 3 3L20 7"/>
                </svg>
              </span>
              {{ $t('sidebar.items.analytics') }}
            </a>

            <a v-if="can('dashboard')" href="#" class="ddm-item" @click.prevent="goRouteM('Dashboard')">
              <span class="ddm-item__icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round">
                  <rect x="3" y="3" width="7.5" height="7.5" rx="1.6"/>
                  <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.6"/>
                  <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.6"/>
                  <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.6"/>
                </svg>
              </span>
              {{ $t('accountMenu.items.home') }}
            </a>

            <a v-if="can('reservations')" href="#" class="ddm-item" @click.prevent="goRouteM('Reservas')">
              <span class="ddm-item__icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="4.5" width="18" height="16.5" rx="2"/>
                  <path d="M8 2.6v3.8M16 2.6v3.8M3 10h18"/>
                  <polyline points="9 14.8 11 16.8 15 12.8"/>
                </svg>
              </span>
              {{ $t('accountMenu.items.bookings') }}
            </a>

            <p class="ddm-group">{{ $t('accountMenu.groups.manage') }}</p>

            <a v-if="can('account')" href="#" class="ddm-item" @click.prevent="goRouteM('accountWelcome')">
              <span class="ddm-item__icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </span>
              {{ $t('accountMenu.items.myAccount') }}
            </a>

            <a v-if="can('properties')" href="#" class="ddm-item" @click.prevent="goRouteM('ListingManager')">
              <span class="ddm-item__icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
              </span>
              {{ $t('accountMenu.items.myAccommodations') }}
            </a>

            <a v-if="can('ai_agent')" href="#" class="ddm-item" @click.prevent="goRouteM('AgentIAManager')">
              <span class="ddm-item__icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M15 4V2M8 9h2M20 9h2M17.8 11.8L19 13M3 21l9-9M12.2 6.2L11 5"/>
                  <path d="M15 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/>
                </svg>
              </span>
              {{ $t('accountMenu.items.aiReception') }}
            </a>

            <a v-if="can('direct_bookings')" href="#" class="ddm-item" @click.prevent="goRouteM('DirectBookingsSettings')">
              <span class="ddm-item__icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="9"/>
                  <path d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18"/>
                </svg>
              </span>
              {{ $t('accountMenu.items.directBookings') }}
            </a>

            <!-- PÁGINA WEB — ya publicada. Son DOS bloques, este y su gemelo de
                 escritorio más abajo: si se vuelve a esconder, hay que comentar
                 los dos o los menús quedan distintos. -->
            <a v-if="can('website')" href="#" class="ddm-item" @click.prevent="goRouteM('PaginasWeb')">
              <span class="ddm-item__icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9h18"/>
                  <circle cx="6.5" cy="6.5" r=".6" fill="currentColor"/><circle cx="9" cy="6.5" r=".6" fill="currentColor"/>
                  <path d="M7 13h6M7 16h9"/>
                </svg>
              </span>
              {{ $t('accountMenu.items.website') }}
            </a>

            <div v-if="!isSubUser" class="ddm-sep"></div>

            <!-- RECOMIENDA Y GANA — dos bloques, este y su gemelo de escritorio más
                 abajo. Oculto para colaboradores: el crédito es del titular. -->
            <a v-if="!isSubUser" href="#" class="ddm-item" @click.prevent="goRouteM('referralProgram')">
              <span class="ddm-item__icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="8" width="18" height="4" rx="1"/>
                  <path d="M12 8v13M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/>
                  <path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5"/>
                </svg>
              </span>
              {{ $t('referrals.menu.label') }}
            </a>

            <div class="ddm-sep"></div>

            <button type="button" class="ddm-item ddm-item--danger" @click="logout">
              <span class="ddm-item__icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
              </span>
              {{ $t('accountMenu.items.logout') }}
            </button>
          </nav>
        </div>
      </div>

      <!-- ── DROPDOWN ────────────────────────────────────────── -->
      <div class="lg dropdown-menu dd-menu user dropdown-menu-right dropdown-menu-arrow">

        <!-- Header usuario -->
        <div class="dd-header">
          <div class="dd-header__avatar">
            <div
              v-if="userRoot.logo"
              class="dd-header__avatar-img"
              :style="{ backgroundImage: `url(${$base.server + userRoot.logo})` }"
            />
            <div v-else class="dd-header__avatar-letter">{{ ownerInitial }}</div>
          </div>
          <div class="dd-header__info">
            <div class="dd-header__name">
              {{ $store.state.subUser ? $store.state.subUser.name : userRoot.name }}
            </div>
            <div v-if="$store.state.subUser" class="dd-header__sub">
              {{ $t('accountMenu.header.collaboratorAccess') }}
            </div>
            <div v-else-if="userRoot.email" class="dd-header__sub">
              {{ userRoot.email }}
            </div>
          </div>
        </div>

        <!-- ── Panel switcher ── -->
        <div v-if="showPanelSwitcher" class="dd-segment-wrap">
          <span class="dd-segment-wrap__label">{{ $t('accountMenu.panel.label') }}</span>
          <div class="dd-segment">
            <!-- Indicador deslizante -->
            <div
              class="dd-segment__indicator"
              :style="activePanelType === 'hotel' ? 'transform: translateX(100%)' : 'transform: translateX(0%)'"
            />
            <button
              class="dd-segment__btn"
              :class="{ 'dd-segment__btn--active': activePanelType === 'apartment' }"
              @click.stop="switchPanel('apartment')"
              type="button"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
              {{ $t('accountMenu.panel.apartment') }}
            </button>
            <button
              class="dd-segment__btn"
              :class="{ 'dd-segment__btn--active': activePanelType === 'hotel' }"
              @click.stop="switchPanel('hotel')"
              type="button"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                <rect x="2" y="7" width="20" height="14" rx="2"/>
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
              </svg>
              {{ $t('accountMenu.panel.hotel') }}
            </button>
          </div>
        </div>

        <!-- El sitio reservado mientras se averigua si esta cuenta tiene los dos tipos.
             Es el marcado de arriba con los textos en transparente: así la altura la
             calcula el MISMO CSS y no un número a mano que mañana se desajuste. -->
        <div v-else-if="esperandoPanelSwitcher" class="dd-segment-wrap" aria-hidden="true">
          <span class="dd-segment-wrap__label dd-hueco dd-hueco--etiqueta">{{ $t('accountMenu.panel.label') }}</span>
          <div class="dd-segment">
            <span class="dd-segment__btn dd-hueco">{{ $t('accountMenu.panel.apartment') }}</span>
            <span class="dd-segment__btn dd-hueco">{{ $t('accountMenu.panel.hotel') }}</span>
          </div>
        </div>

        <div class="dd-divider" />

        <!-- ── Menú de navegación ── -->
        <a v-if="can('account')" href="#" class="dd-item" @click.prevent="goRoute('accountWelcome')">
          <span class="dd-item__icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </span>
          {{ $t('accountMenu.items.myAccount') }}
        </a>

        <a v-if="can('properties')" href="#" class="dd-item" @click.prevent="goListingManager()">
          <span class="dd-item__icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </span>
          {{ $t('accountMenu.items.myAccommodations') }}
        </a>

        <a v-if="can('ai_agent')" href="#" class="dd-item" @click.prevent="goAiAgent()">
          <span class="dd-item__icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M15 4V2M8 9h2M20 9h2M17.8 11.8L19 13M3 21l9-9M12.2 6.2L11 5"/>
              <path d="M15 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/>
            </svg>
          </span>
          {{ $t('accountMenu.items.aiReceptionist') }}
          <span class="dd-badge dd-badge--ia">{{ $t('accountMenu.badges.ai') }}</span>
        </a>

        <a v-if="can('direct_bookings')" href="#" class="dd-item" @click.prevent="goRoute('DirectBookingsSettings')">
          <span class="dd-item__icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418"/>
            </svg>
          </span>
          {{ $t('accountMenu.items.directBookings') }}
        </a>

        <!-- PÁGINA WEB — ya publicada. Son DOS bloques, este y su gemelo del menú
             móvil, más arriba en este mismo fichero: si se vuelve a esconder, hay
             que comentar los dos o los menús quedan distintos. -->
        <a v-if="can('website')" href="#" class="dd-item" @click.prevent="goRoute('PaginasWeb')">
          <span class="dd-item__icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="4" width="18" height="16" rx="2"/>
              <path d="M3 9h18"/>
              <circle cx="6.5" cy="6.5" r=".6" fill="currentColor"/>
              <circle cx="9" cy="6.5" r=".6" fill="currentColor"/>
              <path d="M7 13h6M7 16h9"/>
            </svg>
          </span>
          {{ $t('accountMenu.items.website') }}
          <span class="dd-badge dd-badge--ia">{{ $t('accountMenu.badges.new') }}</span>
        </a>

        <div v-if="!isSubUser" class="dd-divider" />

        <!-- RECOMIENDA Y GANA — gemelo del bloque del menú móvil (más arriba). -->
        <a v-if="!isSubUser" href="#" class="dd-item" @click.prevent="goRoute('referralProgram')">
          <span class="dd-item__icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="8" width="18" height="4" rx="1"/>
              <path d="M12 8v13M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/>
              <path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5"/>
            </svg>
          </span>
          {{ $t('referrals.menu.label') }}
          <span class="dd-badge dd-badge--ia">{{ $t('referrals.menu.badge') }}</span>
        </a>

        <div class="dd-divider" />

        <a href="#" class="dd-item dd-item--danger" @click.prevent="logout">
          <span class="dd-item__icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </span>
          {{ $t('accountMenu.items.logout') }}
        </a>


      </div>
    </div>

    <modal-limit-exceeded />
  </div>
</template>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800&display=swap");

/* ── Variables ──────────────────────────────────────────── */
$font:       'DM Sans', system-ui, sans-serif;
$green:      #11b14d;
$green-soft: #f0fdf4;
$indigo:     #4f46e5;
$purple:     #673de6;
$border:     #edf1f5;
$text-main:  #111827;
$text-sub:   #6b7280;
$radius:     14px;

/*
 * El hueco con el icono de la izquierda. Estaba en linea (`style="margin-left:
 * 10px"`) y por eso ninguna hoja podia apretarlo en pantalla estrecha: un estilo
 * en linea gana a cualquier regla sin `!important`. Aqui va 12px, la misma
 * medida que separa a los botones hermanos de la barra; en estrecho lo pone a 0
 * la regla de 991px de `Maqueta/Nav.vue`, que ya reparte con `gap`.
 */
.the-navbar__user-meta {
  margin-left: 12px;
}

/* Fuente base para todo el dropdown */
.dd-menu, .dd-menu *, .dd-trigger, .dd-trigger * {
  font-family: $font !important;
}

/* ══════════════════════════════════════════════════════════
   TRIGGER
══════════════════════════════════════════════════════════ */
.dd-trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 12px;
  transition: background 0.15s;
  user-select: none;

  &:hover { background: #f4f5f7; }
}

.dd-avatar {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  overflow: hidden;

  &__img {
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
  }

  &__letter {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #673de6 0%, #4c23c0 100%);
    color: #fff;
    font-weight: 800;
    font-size: 15px;
    letter-spacing: -0.5px;
  }
}

.dd-trigger__info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
}

.dd-trigger__name {
  font-size: 13.5px;
  font-weight: 700;
  color: $text-main;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
  line-height: 1.2;
}

.dd-trigger__role {
  font-size: 10.5px;
  font-weight: 600;
  color: #94a3b8;
  margin-top: 1px;
  line-height: 1.3;
  letter-spacing: 0;
}

.dd-trigger__icon {
  color: #9ca3af;
  flex-shrink: 0;
  transition: color 0.15s;
}
.dd-trigger:hover .dd-trigger__icon { color: $text-main; }

/* ══════════════════════════════════════════════════════════
   DROPDOWN PANEL
══════════════════════════════════════════════════════════ */
.dd-menu {
  width: 300px !important;
  border-radius: $radius !important;
  border: 1px solid $border !important;
  box-shadow: 0 12px 40px rgba(0,0,0,.12), 0 4px 12px rgba(0,0,0,.06) !important;
  padding: 8px 0 10px !important;
  margin-top: 10px !important;
  overflow: visible !important;
}

/* ── Header ────────────────────────────────────────────── */
.dd-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px 14px;

  &__avatar {
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    border-radius: 13px;
    overflow: hidden;
  }

  &__avatar-img {
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
  }

  &__avatar-letter {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #673de6 0%, #4c23c0 100%);
    color: #fff;
    font-weight: 800;
    font-size: 18px;
    letter-spacing: -0.5px;
  }

  &__info {
    min-width: 0;
    flex: 1;
  }

  &__name {
    font-size: 15px;
    font-weight: 700;
    color: $text-main;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__sub {
    font-size: 11.5px;
    color: $text-sub;
    margin-top: 2px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

/* ── Segment control (panel switcher) ───────────────────── */
.dd-segment-wrap {
  margin: 4px 10px 2px;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid $border;
  border-radius: 10px;

  &__label {
    display: block;
    font-size: 10px;
    font-weight: 700;
    color: $text-sub;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    margin-bottom: 9px;
  }
}

/* Un hueco que respira, para que se lea «esto está cargando» y no «esto está roto».
   El texto va en transparente: ocupa lo mismo que ocupará de verdad. */
.dd-hueco {
  color: transparent !important;
  user-select: none;
  pointer-events: none;
  border-radius: 6px;
  background: linear-gradient(90deg, #e9edf2 25%, #f4f7fa 37%, #e9edf2 63%);
  background-size: 400% 100%;
  animation: dd-hueco-brillo 1.4s ease infinite;

  &--etiqueta {
    display: inline-block;
    height: 10px;
  }
}

@keyframes dd-hueco-brillo {
  0%   { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

/* Quien pidió menos movimiento no lo ve parpadear; el hueco sigue estando. */
@media (prefers-reduced-motion: reduce) {
  .dd-hueco { animation: none; }
}

.dd-segment {
  position: relative;
  display: flex;
  background: #e8ecf0;
  border-radius: 8px;
  padding: 3px;
  gap: 0;

  &__indicator {
    position: absolute;
    top: 3px;
    left: 3px;
    width: calc(50% - 3px);
    height: calc(100% - 6px);
    background: #fff;
    border-radius: 6px;
    box-shadow: 0 1px 4px rgba(0,0,0,.12), 0 0 0 1px rgba(0,0,0,.04);
    transition: transform 0.22s cubic-bezier(0.34, 1.2, 0.64, 1);
    pointer-events: none;
  }

  &__btn {
    position: relative;
    z-index: 1;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 6px 10px;
    border: none;
    background: transparent;
    font-family: $font;
    font-size: 12px;
    font-weight: 600;
    color: #94a3b8;
    cursor: pointer;
    border-radius: 6px;
    transition: color 0.18s;
    white-space: nowrap;

    svg { flex-shrink: 0; transition: color 0.18s; }

    &--active {
      color: $text-main;
      svg { color: $text-main; }
    }

    &:not(.dd-segment__btn--active):hover {
      color: #64748b;
    }
  }
}

/* ── Divisor ────────────────────────────────────────────── */
.dd-divider {
  height: 1px;
  background: $border;
  margin: 6px 10px;
}

/* ── Items del menú ─────────────────────────────────────── */
.dd-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 16px;
  margin: 1px 6px;
  border-radius: 9px;
  font-size: 13.5px;
  font-weight: 500;
  color: $text-main;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.13s;
  width: calc(100% - 12px);

  &:hover {
    background: #f3f4f6;
    color: $text-main;
    text-decoration: none;
  }

  &--danger {
    color: #6b7280;
    &:hover { background: #fef2f2; color: #dc2626; .dd-item__icon { color: #dc2626 !important; } }
  }
}

.dd-item__icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #374151;
  transition: background 0.13s, color 0.13s;
}

/* ── Badge "Nuevo" ──────────────────────────────────────── */
.dd-badge {
  margin-left: auto;
  font-size: 9.5px;
  font-weight: 800;
  border-radius: 999px;
  padding: 2px 8px;
  line-height: 1.4;
  letter-spacing: 0.04em;
  text-transform: uppercase;

  &--new {
    background: #eef2ff;
    border: 1px solid #c7d2fe;
    color: $indigo;
  }

  &--ia {
    background: #f5f3ff;
    border: 1px solid #e9e3ff;
    color: #673de6;
  }
}

/* ── Sección crear ──────────────────────────────────────── */
.dd-create-section {
  padding: 6px 10px 2px;

  &__label {
    font-size: 10px;
    font-weight: 700;
    color: $text-sub;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin: 0 4px 6px;
  }
}

.dd-create-btns {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.dd-create-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1.5px solid $border;
  background: #fff;
  cursor: pointer;
  font-family: $font;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  text-align: left;
  width: 100%;

  &:hover {
    border-color: darken($border, 8%);
    background: #f8fafc;
    box-shadow: 0 2px 8px rgba(0,0,0,.05);

    .dd-create-btn__arrow { opacity: 1; transform: translateX(2px); }
  }

  &__icon {
    width: 34px;
    height: 34px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &--listing {
      background: #dcfce7;
      color: $green;
    }

    &--unit {
      background: #ede9fe;
      color: #7c3aed;
    }
  }

  &__text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  &__title {
    font-size: 13px;
    font-weight: 700;
    color: $text-main;
    line-height: 1.2;
  }

  &__sub {
    font-size: 11px;
    color: $text-sub;
    font-weight: 500;
    margin-top: 1px;
    line-height: 1.3;
  }

  &__arrow {
    flex-shrink: 0;
    color: #d1d5db;
    opacity: 0;
    transition: opacity 0.15s, transform 0.15s;
  }
}

/* ── Responsive ─────────────────────────────────────────── */
@media (max-width: 720px) {
  .dd-menu { width: 290px !important; }
  .dd-trigger__info { display: none; }
}
</style>

<!-- Ocultar caret Bootstrap globalmente para este componente -->
<style>
.dd-trigger.dropdown-toggle::after { display: none !important; }
</style>

<style lang="scss" scoped>
/* ── Menú móvil propio (overlay deslizante desde la derecha) ── */
.ddm-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  justify-content: flex-end;
}
.ddm-panel {
  width: 86%;
  max-width: 340px;
  height: 100%;
  background: #fff;
  display: flex;
  flex-direction: column;
  font-family: "DM Sans", system-ui, -apple-system, sans-serif;
  box-shadow: -14px 0 44px rgba(0, 0, 0, 0.22);
  animation: ddmIn 0.24s cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes ddmIn {
  from { transform: translateX(100%); }
  to   { transform: none; }
}
/* ── Un solo rail a la izquierda ───────────────────────────────────────────────
   La cabecera empezaba a 16 px del borde, las etiquetas de grupo a 24 y el icono
   de cada entrada a 22: tres líneas verticales distintas en un panel de 340 px.
   Por separado cada una parecía bien; juntas, el menú se veía desalineado sin que
   se supiera señalar por qué. Ahora las tres arrancan en el mismo sitio. */
.ddm-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px 14px;
  border-bottom: 1px solid #eef0f4;
}
.ddm-user { display: flex; align-items: center; gap: 11px; }
.ddm-user__avatar {
  width: 42px; height: 42px; border-radius: 12px;
  /* Morado de la marca. Estaba en #5e72e4, que es el de la capa vieja de Argon. */
  background: #673de6; color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 16px;
}
.ddm-user__name { font-weight: 700; font-size: 15px; color: #1a1f2e; }
.ddm-user__role { font-size: 12px; color: #8a90a2; }
.ddm-close {
  width: 34px; height: 34px; border-radius: 10px; border: none;
  background: #f3f4f7; color: #475467; font-size: 16px; cursor: pointer;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.ddm-items {
  display: flex; flex-direction: column;
  padding: 8px 10px 18px; gap: 2px; overflow-y: auto;
}

/* Rótulo de grupo. Siete enlaces seguidos no se leen; en dos bloques, sí. */
.ddm-group {
  margin: 16px 12px 7px;   /* 12 + los 10 del contenedor = los mismos 22 */
  font-size: 10px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase;
  color: #9aa2b1;
}
.ddm-group:first-child { margin-top: 6px; }

.ddm-item {
  display: flex; align-items: center; gap: 12px;
  padding: 11px 12px; border-radius: 11px;
  font-family: inherit;
  font-size: 14.5px; font-weight: 600; letter-spacing: -0.01em; color: #2d3344;
  background: none; border: none; text-align: left; width: 100%;
  cursor: pointer; text-decoration: none;
  transition: background 0.15s ease;
}

/* El chip de icono de la casa: cuadrado redondeado, fondo tintado y trazo de
   marca. El mismo que usan el asistente, las novedades y el resto del panel. */
.ddm-item__icon {
  width: 30px; height: 30px; flex-shrink: 0; border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  background: #f3efff; color: #673de6;
}
.ddm-item:active { background: #f6f7f9; }

.ddm-sep { height: 1px; background: #eef0f4; margin: 12px 12px 10px; }

.ddm-item--danger { color: #d92d20; }
.ddm-item--danger .ddm-item__icon { background: #fdecea; color: #d92d20; }
</style>
